import type { ComposerCommand } from "@design";

/**
 * Something wrong with an agent's `<slash_commands>` block, as a code the preview translates. `position` is the
 * `<command>` it's about, counting from 1 (0 for the block as a whole): with `code`, a stable key.
 */
export type SlashCommandProblem = { position: number } & (
  | { code: "empty" }
  | { code: "malformed" }
  | { code: "noCommands" }
  | { code: "missingName" }
  | { code: "spaces"; name: string }
  | { code: "noDescription"; name: string }
);

/** A command as people will be offered it, with its place in the block (a stable key while it's being written). */
export type ParsedCommand = ComposerCommand & { position: number };

export interface SlashCommandsParse {
  /** The prompt has a `<slash_commands>` block at all. */
  hasBlock: boolean;
  /** The commands people will be offered, in order. */
  commands: ParsedCommand[];
  problems: SlashCommandProblem[];
}

const BLOCK = /<slash_commands>([\s\S]*?)<\/slash_commands>/;

/**
 * The agent's slash commands as the backend reads them (`backend/src/services/ai/agents/slash_commands.py`), so the
 * editor can preview them and say what's wrong before saving. Like the backend, it leaves out a command with no name
 * and keeps the rest; a name with a space (it can be picked from the list, but typing the space closes the list) or a
 * command without a description gets a warning.
 */
export function parseSlashCommands(systemPrompt: string): SlashCommandsParse {
  const block = BLOCK.exec(systemPrompt)?.[1];
  if (block === undefined) return { hasBlock: false, commands: [], problems: [] };
  const inner = block.trim();
  if (!inner) return { hasBlock: true, commands: [], problems: [{ code: "empty", position: 0 }] };

  const doc = new DOMParser().parseFromString(`<root>${inner}</root>`, "text/xml");
  if (doc.querySelector("parsererror"))
    return { hasBlock: true, commands: [], problems: [{ code: "malformed", position: 0 }] };
  const elements = Array.from(doc.querySelectorAll("command"));
  if (elements.length === 0) return { hasBlock: true, commands: [], problems: [{ code: "noCommands", position: 0 }] };

  const commands: ParsedCommand[] = [];
  const problems: SlashCommandProblem[] = [];
  elements.forEach((element, index) => {
    const position = index + 1;
    const name = (element.getAttribute("name") ?? "").trim();
    const description = (element.getAttribute("description") ?? "").trim();
    if (!name) {
      problems.push({ code: "missingName", position });
      return;
    }
    if (/\s/.test(name)) problems.push({ code: "spaces", name, position });
    if (!description) problems.push({ code: "noDescription", name, position });
    commands.push({ name, description, position });
  });
  return { hasBlock: true, commands, problems };
}
